/**
 * Dialog for uploading transliteration data files
 */

import React, { useRef, useState } from 'react'
import {
  DialogOverlay,
  DialogOverlayFooter,
  DialogOverlayActions,
  Button,
  Paragraph,
  MarginBox,
  Row,
  colors,
  Separator,
  DataBlock,
  BillboardText,
} from '@fs/zion-ui'
import type { UseOverlayReturnOptions } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { ContentUpload } from '@fs/zion-icon'
import { useTransliterationEval } from '../../TransliterationEvalProvider'

interface FileUploadDialogProps {
  overlay: UseOverlayReturnOptions
}

const fileInputContainerCss = (isDragging: boolean): ReturnType<typeof css> => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  border: 2px dashed ${isDragging ? colors.blue50 : colors.gray30};
  border-radius: 8px;
  background-color: ${isDragging ? colors.blue05 : colors.gray05};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.blue50};
    background-color: ${colors.blue05};
  }
`

const fileInputCss = css`
  display: none;
`

export const FileUploadDialog: React.FC<FileUploadDialogProps> = ({ overlay }) => {
  const [t] = useTranslation()
  const { uploadFile, isUploading, uploadError } = useTransliterationEval()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)

    const file = event.dataTransfer.files?.[0]
    if (file?.name.endsWith('.json')) {
      setSelectedFile(file)
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }

  const handleUpload = async (e: React.MouseEvent | React.UIEvent): Promise<void> => {
    if (!selectedFile) return

    try {
      await uploadFile(selectedFile)
      setSelectedFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      // Close overlay and return focus to button
      overlay.handleClick(e)
    } catch (err) {
      // Error is handled by provider
      // eslint-disable-next-line no-console
      console.error('Upload failed:', err)
    }
  }

  const handleContainerClick = (e?: React.MouseEvent<HTMLDivElement>): void => {
    e?.stopPropagation()
    fileInputRef.current?.click()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleContainerClick()
    }
  }

  return (
    <DialogOverlay {...overlay} title={t('transliteration-eval-page.file-upload-dialog.title', 'Load Batch')}>
      <MarginBox marginX="md" marginY="md">
        <Paragraph>
          {t(
            'transliteration-eval-page.file-upload-dialog.instruction.message',
            'Select a JSON file or drag and drop it below.'
          )}
        </Paragraph>

        <MarginBox marginY="md">
          <div
            css={fileInputContainerCss(isDragging)}
            onClick={(e) => handleContainerClick(e)}
            onKeyDown={handleKeyDown}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            role="button"
            tabIndex={0}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              css={fileInputCss}
              aria-label={t('transliteration-eval-page.file-upload-dialog.file-input.aria-label', 'File input')}
            />

            <Row alignX="center">
              <ContentUpload size="lg" />
            </Row>
            <Separator size="xs" />

            <Paragraph centered size="sm">
              {selectedFile
                ? t('transliteration-eval-page.file-upload-dialog.click-change-file.message', 'Click to change file')
                : t(
                    'transliteration-eval-page.file-upload-dialog.drag-drop.message',
                    'Click to select file or drag & drop'
                  )}
            </Paragraph>

            <Paragraph centered size="xs">
              {t('transliteration-eval-page.file-upload-dialog.format-info.message', 'Supported format: .json')}
            </Paragraph>
          </div>

          {selectedFile && (
            <>
              <Separator size="xs" />
              <Row alignX="center">
                <DataBlock
                  inline
                  heading={t('transliteration-eval-page.file-upload-dialog.selected.label', 'Selected:')}
                  data={selectedFile.name}
                />
              </Row>
            </>
          )}

          {uploadError && (
            <>
              <Separator size="xs" />
              <BillboardText primaryColor={colors.red70}>
                <Row alignX="center">
                  <DataBlock
                    inline
                    heading={t('transliteration-eval-page.file-upload-dialog.error.label', 'Error:')}
                    data={uploadError}
                  />
                </Row>
              </BillboardText>
            </>
          )}
        </MarginBox>
      </MarginBox>

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button onClick={overlay.handleClick} emphasis="low" disabled={isUploading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleUpload} disabled={!selectedFile || isUploading}>
            {isUploading ? t('common.uploading', 'Uploading...') : t('common.upload', 'Upload')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
